import { FAQ } from "../About/components/AboutFAQ";
import SEOMeta from "../../components/SEOMeta";
import { WebPaths } from "../../../../routes/data";
import { SVLogoSquare } from "../../../../images";
import { IconType } from "react-icons";
import { AiOutlineComment } from "react-icons/ai";
import { BsPassport } from "react-icons/bs";
import { MdOutlineBook } from "react-icons/md";
import { RiBriefcaseLine } from "react-icons/ri";
import { TbMessageQuestion } from "react-icons/tb";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQGroup {
  id: string;
  label: string;
  headingStart: string;
  headingHighlight: string;
  subtitle: string;
  icon: IconType;
  items: FAQItem[];
}

const faqGroups: FAQGroup[] = [
  {
    id: "general",
    label: "General",
    headingStart: "General",
    headingHighlight: "Questions",
    subtitle:
      "Getting started with SeamlessVisa, how support works, and what to expect from our platform.",
    icon: TbMessageQuestion,
    items: [
      {
        question: "How do I get started with SeamlessVisa?",
        answer:
          "Start by choosing the service that matches your goal: Study Abroad, Visa Services, or FiBi interview preparation. After that, book a consultation or begin your guided flow directly on the platform.",
      },
      {
        question: "Do you guarantee visa approval or admission outcomes?",
        answer:
          "No provider can guarantee final decisions from embassies, consulates, or schools. We focus on improving application quality, preparation, and readiness so you can submit the strongest possible case.",
      },
      {
        question: "Can I use SeamlessVisa from outside my current country?",
        answer:
          "Yes. Our services are designed for global users and remote support, so you can plan your study or visa journey from wherever you are.",
      },
      {
        question: "How does SeamlessVisa handle my data security?",
        answer:
          "We use controlled access, structured workflows, and secure processing practices to protect account and application data. You can also review our privacy and legal policy pages for full details.",
      },
      {
        question: "What is FiBi and when should I use it?",
        answer:
          "FiBi is our AI interview trainer for visa interview practice. Use it before your interview to rehearse realistic questions, sharpen your answers, and build confidence.",
      },
    ],
  },
  {
    id: "visa-support",
    label: "Visa Support",
    headingStart: "Visa",
    headingHighlight: "Support",
    subtitle:
      "Answers about denials, dependent applications, and account-level data requests.",
    icon: BsPassport,
    items: [
      {
        question: "Can you help if my visa was denied before?",
        answer:
          "Yes. We review your previous submission, identify possible weak points, and help you prepare a stronger reapplication strategy with clearer supporting documentation.",
      },
      {
        question: "Can SeamlessVisa support visa applications for dependents?",
        answer:
          "Yes. Dependent support is available for eligible pathways, and we can guide family-linked documentation and interview preparation where required.",
      },
      {
        question: "Can I request deletion of my personal data?",
        answer:
          "Yes. You can submit a data request through support, and the team will process it according to applicable privacy policy terms and platform obligations.",
      },
    ],
  },
  {
    id: "study-abroad",
    label: "Study Abroad",
    headingStart: "Study",
    headingHighlight: "Abroad",
    subtitle:
      "Planning, admissions support, visa steps, and tuition guidance for your destination options.",
    icon: MdOutlineBook,
    items: [
      {
        question: "Can you help me find and apply for scholarships?",
        answer:
          "Yes. We help identify scholarship options that fit your profile and guide you through application requirements, timelines, and supporting documents.",
      },
      {
        question: "What if I am unsure which country is best for me?",
        answer:
          "We compare destinations based on your budget, academic goals, and long-term plans, then help you choose the strongest-fit study path.",
      },
      {
        question: "Do I still need a visa after getting school admission?",
        answer:
          "Yes. Admission and visa approval are separate steps. Once admission is secured, you still need to complete the visa process for your destination.",
      },
      {
        question: "Do you support both undergraduate and postgraduate applicants?",
        answer:
          "Yes. We support undergraduate, postgraduate, and specialized academic pathways across supported destinations.",
      },
      {
        question: "Can I apply to schools in more than one country at the same time?",
        answer:
          "Yes. Multi-country application planning is supported. We help you structure priorities, timelines, and documentation across your selected options.",
      },
      {
        question: "Can you assist with tuition payment planning and proof of funds?",
        answer:
          "Yes. We guide documentation strategy for financial evidence and tuition planning requirements tied to admissions and visa expectations.",
      },
    ],
  },
  {
    id: "fibi",
    label: "FiBi",
    headingStart: "FiBi",
    headingHighlight: "FAQs",
    subtitle:
      "How FiBi simulations work, the feedback you receive, and privacy expectations.",
    icon: AiOutlineComment,
    items: [
      {
        question: "How realistic are FiBi interview simulations?",
        answer:
          "FiBi is designed to simulate real consular interview flow with practical, category-relevant prompts so your practice feels close to live interview conditions.",
      },
      {
        question: "Does FiBi give specific feedback on my answers?",
        answer:
          "Yes. FiBi provides targeted feedback to improve clarity, confidence, and structure, so you can refine weak responses before your actual interview.",
      },
      {
        question: "Is my FiBi session data kept private?",
        answer:
          "Yes. Session records are treated as private user data and handled under SeamlessVisa's platform privacy and data governance controls.",
      },
    ],
  },
  {
    id: "business-services",
    label: "Business Services",
    headingStart: "Business",
    headingHighlight: "Services",
    subtitle:
      "Support for organizations handling larger caseloads and team-based immigration workflows.",
    icon: RiBriefcaseLine,
    items: [
      {
        question: "Do you support bulk visa processing for organizations?",
        answer:
          "Yes. We support organizations managing multiple applicants and can help structure repeatable case workflows for scale.",
      },
      {
        question: "How does your B2B service approach work?",
        answer:
          "We align support to your organization model, including process mapping, documentation coordination, and visibility across active application pipelines.",
      },
      {
        question: "Can your team work with our internal team and process tools?",
        answer:
          "Yes. We collaborate with internal teams and can adapt engagement flow to your operating model for smoother integration and delivery.",
      },
    ],
  },
];

const FAQs = () => {
  return (
    <>
      <SEOMeta
        title="FAQs | SeamlessVisa"
        description="Find answers to common questions about SeamlessVisa services, visa support, study abroad, FiBi interview prep, and business solutions."
        canonicalUrl={`https://www.seamlessvisa.com${WebPaths.FAQs}`}
        url={`https://www.seamlessvisa.com${WebPaths.FAQs}`}
        siteName="SeamlessVisa"
        publisher="https://www.facebook.com/share/1BhLA5r2Ac/"
        imageUrl={SVLogoSquare}
        imageWidth="1920"
        imageHeight="1080"
        imageType="image/png"
        twitterCard="summary_large_image"
        twitterTitle="FAQs | SeamlessVisa"
        twitterDescription="Find answers to common questions about SeamlessVisa services, visa support, study abroad, FiBi interview prep, and business solutions."
        twitterSite="@SeamlessVisa_"
      />

      <div className="relative overflow-hidden bg-white-f9">
        <section className="bg-white-f9 py-12 sm:py-16">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-grey-eb bg-gradient-to-br from-white via-blue-ea/25 to-white p-8 text-center shadow-sm sm:p-12">
              <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-blue-ea/50 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-10 -left-6 h-24 w-24 rounded-full bg-blue-ea/60 blur-2xl" />
              <p className="inline-flex items-center gap-2 rounded-full border border-blue-ea bg-white px-4 py-2 text-xs font-semibold uppercase tracking-widest text-blue-00">
                <TbMessageQuestion className="h-4 w-4" /> Support Center
              </p>
              <h1 className="mt-5 text-h1 font-light font-lato text-black-1a">
                Frequently Asked
                <span className="mx-2 inline-block rotate-1 rounded-2xl border-[6px] border-blue-ea bg-blue-00 px-4 py-0.5 align-middle italic text-white">
                  Questions
                </span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-dark-6b sm:text-lg">
                Browse by topic to quickly find clear answers on services,
                applications, preparation, and support.
              </p>
            </div>
          </div>
        </section>

        {faqGroups.map((group, index) => {
          const GroupIcon = group.icon;

          return (
            <FAQ
              key={group.id}
              idPrefix={`faqs-${group.id}`}
              items={group.items}
              label={
                <span className="inline-flex items-center gap-2">
                  <GroupIcon className="h-4 w-4" /> {group.label}
                </span>
              }
              labelClassName="inline-flex rounded-full border border-blue-ea bg-white px-4 py-2 text-xs font-semibold uppercase tracking-widest text-blue-00"
              title={
                <>
                  {group.headingStart}
                  <span className="mx-1 text-blue-00 italic">
                    {group.headingHighlight}
                  </span>
                </>
              }
              titleClassName="mt-4 text-4xl font-light font-lato leading-tight text-black-1a sm:text-5xl"
              subtitle={group.subtitle}
              subtitleClassName="mt-4 text-base leading-7 text-dark-6b sm:text-lg"
              containerClassName={
                index % 2 === 0
                  ? "bg-white-f9 py-16 sm:py-20"
                  : "bg-white py-16 sm:py-20"
              }
            />
          );
        })}
      </div>
    </>
  );
};

export default FAQs;
